import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, RefreshCw, Users, Clock, AlertTriangle,
  CheckCircle, Circle, Activity, Shield
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  in_progress: { color: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50', label: 'Testing' },
  idle: { color: 'bg-gray-300', text: 'text-gray-500', bg: 'bg-gray-50', label: 'Idle' },
};

const LiveClassMonitor = ({ classId, className, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastPoll, setLastPoll] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/classes/${classId}/live-monitor`);
      setData(res.data);
      setLastPoll(new Date());
    } catch (e) {
      console.error('Monitor poll error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [classId]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, 10000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh]);

  if (loading) {
    return <div className="p-8 text-center text-gray-400">Loading monitor...</div>;
  }

  const students = data?.students || [];
  const active = students.filter(s => s.status === 'in_progress');
  const idle = students.filter(s => s.status === 'idle');

  return (
    <div data-testid="live-monitor">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2" data-testid="monitor-back-btn">
              <ArrowLeft size={18} />
            </Button>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="monitor-title">
              Live Classroom Monitor
            </h2>
            {autoRefresh && (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1">
                <Activity size={12} className="animate-pulse" /> Live
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 ml-11">
            {data?.class_name} &bull; {data?.active_count || 0} of {data?.total_students || 0} students actively testing
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className={`h-8 ${autoRefresh ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="toggle-auto-refresh"
          >
            <RefreshCw size={14} className={`mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={fetchData} data-testid="manual-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-3 gap-0 border-t border-l border-gray-300 mb-6" data-testid="monitor-summary">
        <div className="border-b border-r border-gray-300 p-4 flex items-center gap-3">
          <Users className="w-6 h-6 text-blue-600" />
          <div>
            <div className="text-xl font-bold">{data?.total_students || 0}</div>
            <div className="text-xs text-gray-500">Total Students</div>
          </div>
        </div>
        <div className="border-b border-r border-gray-300 p-4 flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <div>
            <div className="text-xl font-bold text-green-600">{data?.active_count || 0}</div>
            <div className="text-xs text-gray-500">Currently Testing</div>
          </div>
        </div>
        <div className="border-b border-r border-gray-300 p-4 flex items-center gap-3">
          <Circle className="w-6 h-6 text-gray-400" />
          <div>
            <div className="text-xl font-bold text-gray-500">{idle.length}</div>
            <div className="text-xs text-gray-500">Idle</div>
          </div>
        </div>
      </div>

      {/* Active Students */}
      {active.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-bold text-green-700 uppercase tracking-wider mb-3">
            Currently Testing ({active.length})
          </h3>
          <div className="space-y-2" data-testid="active-students">
            {active.map(s => (
              <div key={s.student_id} className="border-2 border-green-200 bg-green-50 p-4" data-testid={`student-active-${s.student_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-500 text-white flex items-center justify-center font-bold text-sm">
                      {s.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{s.name}</div>
                      <div className="text-xs text-gray-600">
                        {s.current_modality && <span className="capitalize">{s.current_modality}</span>}
                        {s.current_modality && ' • '}
                        Grade {s.grade_level}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <div className="font-bold">{s.questions_answered}</div>
                      <div className="text-xs text-gray-500">Answered</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold flex items-center gap-1">
                        <Clock size={12} /> {s.elapsed_minutes}m
                      </div>
                      <div className="text-xs text-gray-500">Elapsed</div>
                    </div>
                    {s.integrity_warnings > 0 && (
                      <div className="text-center">
                        <div className="font-bold text-red-600 flex items-center gap-1">
                          <AlertTriangle size={12} /> {s.integrity_warnings}
                        </div>
                        <div className="text-xs text-red-500">Warnings</div>
                      </div>
                    )}
                    {s.integrity_warnings === 0 && (
                      <div className="text-center">
                        <Shield size={16} className="text-green-500 mx-auto" />
                        <div className="text-xs text-green-600">Clean</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Idle Students */}
      {idle.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
            Idle ({idle.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="idle-students">
            {idle.map(s => (
              <div key={s.student_id} className="border border-gray-200 p-3 bg-gray-50" data-testid={`student-idle-${s.student_id}`}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-gray-200 text-gray-500 flex items-center justify-center text-xs font-semibold">
                    {s.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="text-sm">{s.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 text-center text-xs text-gray-400">
        Last updated: {lastPoll ? lastPoll.toLocaleTimeString() : '—'} &bull; Polling every 10s
      </div>
    </div>
  );
};

export default LiveClassMonitor;
